/**
 * Popover der KW-/Tag-Werkzeuge — reference/tagesvorlagen/TAGESVORLAGEN.md:
 * - Vorlagen einsetzen, Woche (§4.1): an der KW-Spalte. Jede angehakte Vorlage fällt auf ihre Wochentage; Vorschau der 7 Tage.
 * - Vorlagen einsetzen, Tag (§5): am Datum einer Tageszelle. Jede Vorlage wählbar, auch außerhalb ihrer Wochentage.
 * - Vorwoche übernehmen (§4): an der KW-Spalte. Vorschau des Bedarfs der Vorwoche, Übernehmen per Button.
 * Eingesetzt wird immer ergänzend (vorhandener Bedarf bleibt), mehrere Vorlagen am Tag werden addiert.
 * Es ist höchstens ein Popover offen; es schließt per Klick daneben, ✕, Abbrechen, Escape oder Scrollen.
 */

import dayjs from '../../../../shared/services/dayjs.js';
import { tagesvorlageService } from '../services/TagesvorlageService.js';
import { bedarfService } from '../services/BedarfService.js';
import { zeigeKleineNachricht } from '../systemNachricht.js';
import { istAppDialogOffen } from '../dialogs/bestaetigung.js';
import { oeffneTagesvorlagen } from '../dialogs/tagesvorlagen.js';
import {
	WOCHENTAGE,
	STANDARD_FARBE,
	formatStunden,
	kennzahlen,
	schichtenText,
	wochentageText,
} from '../util/tagesvorlagen.js';

let offen = null; // { pop, anker, aufraeumen }

function el(tag, className = '', text = '') {
	const e = document.createElement(tag);
	if (className) e.className = className;
	if (text) e.textContent = text;
	return e;
}

/** Stunden kurz für die Vorschau-Kacheln (61 h / 7,5 h). */
function stundenKurz(min) {
	return `${(min / 60).toLocaleString('de-DE', { maximumFractionDigits: 1 })} h`;
}

function minutenVon(vorlagen) {
	return vorlagen.reduce((summe, v) => summe + kennzahlen(v.Schichten).minuten, 0);
}

function farbe(vorlage) {
	return vorlage.Farbe || STANDARD_FARBE;
}

function schliesse() {
	if (!offen) return;
	offen.aufraeumen();
	offen.pop.remove();
	offen.anker.classList.remove('on');
	offen = null;
}

/**
 * Positioniert das Popover (position: fixed) rechts neben ('seite') bzw. unter ('unten') dem Anker, im Viewport
 * gehalten. Der Pfeil zeigt weiter auf den Anker.
 */
function positioniere(pop, anker, art) {
	const r = anker.getBoundingClientRect();
	const rand = 10;
	const breite = pop.offsetWidth;
	const hoehe = pop.offsetHeight;
	let left;
	let top;
	if (art === 'seite') {
		left = r.right + 12;
		top = Math.min(Math.max(rand, r.top - 14), window.innerHeight - hoehe - rand);
		pop.style.setProperty('--ay', `${r.top + r.height / 2 - top - 6}px`);
	} else {
		left = Math.min(Math.max(rand, r.left + r.width / 2 - breite / 2), window.innerWidth - breite - rand);
		top = Math.min(r.bottom + 10, window.innerHeight - hoehe - rand);
		pop.style.setProperty('--ax', `${r.left + r.width / 2 - left - 6}px`);
	}
	pop.style.left = `${Math.max(rand, left)}px`;
	pop.style.top = `${Math.max(rand, top)}px`;
}

/** Öffnet ein leeres Popover am Anker und verdrahtet das Schließen. */
function oeffnePopover(anker, art, breite) {
	const warOffen = offen?.anker === anker;
	schliesse();
	if (warOffen) return null; // zweiter Klick auf denselben Button schließt nur
	const pop = el('div', `wt-pop ${art === 'seite' ? 'side' : 'below'}`);
	pop.style.width = `${breite}px`;
	document.body.appendChild(pop);
	anker.classList.add('on');

	const beiKlick = (e) => { if (!pop.contains(e.target) && !anker.contains(e.target)) schliesse(); };
	const beiTaste = (e) => { if (e.key === 'Escape' && !istAppDialogOffen()) schliesse(); };
	const beiScroll = (e) => { if (!pop.contains(e.target)) schliesse(); };
	document.addEventListener('mousedown', beiKlick);
	document.addEventListener('keydown', beiTaste);
	window.addEventListener('scroll', beiScroll, true);
	window.addEventListener('resize', schliesse);
	offen = {
		pop,
		anker,
		aufraeumen: () => {
			document.removeEventListener('mousedown', beiKlick);
			document.removeEventListener('keydown', beiTaste);
			window.removeEventListener('scroll', beiScroll, true);
			window.removeEventListener('resize', schliesse);
		},
	};
	return pop;
}

function baueKopf(icon, titel, untertitel) {
	const kopf = el('div', 'wt-ph');
	kopf.innerHTML = `<i class="bi ${icon}"></i>`;
	const texte = el('div');
	texte.append(el('div', 'pt', titel), el('div', 'ps', untertitel));
	kopf.appendChild(texte);
	const x = el('button', 'x');
	x.type = 'button';
	x.title = 'Schließen';
	x.innerHTML = '<i class="bi bi-x-lg"></i>';
	x.addEventListener('click', schliesse);
	kopf.appendChild(x);
	return kopf;
}

/** Hinweis, dass am Ziel schon Bedarf liegt — eingesetzt wird trotzdem ergänzend. */
function baueBestehendHinweis(anzahl, ziel) {
	if (!anzahl) return null;
	const hinweis = el('div', 'wt-note info');
	hinweis.innerHTML = '<i class="bi bi-info-circle"></i>';
	hinweis.appendChild(document.createTextNode(`${ziel} enthält bereits ${anzahl} ${anzahl === 1 ? 'Bedarf' : 'Bedarfe'} — Vorlagen werden ergänzt.`));
	return hinweis;
}

/** Eine Vorlage als Checkbox-Zeile (.wt-opt). */
function baueOption(vorlage, gewaehlt, untertitel, mitTagen, onToggle) {
	const opt = el('button', `wt-opt${gewaehlt ? ' on' : ''}${mitTagen ? '' : ' ohne-tage'}`);
	opt.type = 'button';
	opt.setAttribute('aria-pressed', String(gewaehlt));
	const cb = el('span', 'cb');
	if (gewaehlt) cb.innerHTML = '<i class="bi bi-check-lg"></i>';
	const bar = el('span', 'bar');
	bar.style.background = farbe(vorlage);
	const texte = el('div');
	texte.append(el('div', 'on1', vorlage.Name), el('div', 'om', untertitel));
	opt.append(cb, bar, texte);
	if (mitTagen) {
		const tage = el('div', 'tv-days');
		WOCHENTAGE.forEach((label, i) => tage.appendChild(el('span', vorlage.Wochentage.includes(i + 1) ? 'on' : '', label)));
		opt.appendChild(tage);
	}
	opt.addEventListener('click', onToggle);
	return opt;
}

function baueFuss({ mitVerwalten, buttonText, aktiv, onUebernehmen }) {
	const fuss = el('div', 'wt-pf');
	if (mitVerwalten) {
		const lk = el('button', 'lk');
		lk.type = 'button';
		lk.innerHTML = '<i class="bi bi-pencil"></i>Vorlagen verwalten';
		lk.addEventListener('click', () => {
			schliesse();
			oeffneTagesvorlagen();
		});
		fuss.appendChild(lk);
	}
	fuss.appendChild(el('span', 'sp'));
	const abbrechen = el('button', 'dbtn', 'Abbrechen');
	abbrechen.addEventListener('click', schliesse);
	const ok = el('button', 'dbtn primary');
	ok.innerHTML = '<i class="bi bi-check-lg"></i>';
	ok.appendChild(document.createTextNode(buttonText));
	ok.disabled = !aktiv;
	ok.addEventListener('click', async () => {
		ok.disabled = true;
		await onUebernehmen();
		ok.disabled = false;
	});
	fuss.append(abbrechen, ok);
	return fuss;
}

/** Ergebnis melden, Popover schließen, Ansicht neu laden. */
async function setzeEin(einsaetze, onFertig) {
	const ergebnis = await tagesvorlageService.setzeVorlagenEin(einsaetze);
	if (!ergebnis) return;
	zeigeKleineNachricht(`${ergebnis.angelegt} ${ergebnis.angelegt === 1 ? 'Bedarf' : 'Bedarfe'} angelegt`, 1);
	if (ergebnis.uebersprungen.length > 0) {
		const vorlagen = [...new Set(ergebnis.uebersprungen.map((u) => u.Vorlage))].join(', ');
		zeigeKleineNachricht(`${ergebnis.uebersprungen.length} Schicht(en) übersprungen — Posten gibt es nicht mehr (${vorlagen}). Bitte die Vorlage prüfen.`, 0);
	}
	schliesse();
	await onFertig();
}

function baueLeerHinweis(pop) {
	const leer = el('div', 'wt-leer');
	leer.textContent = 'Noch keine Tagesvorlagen angelegt.';
	pop.appendChild(leer);
	pop.appendChild(baueFuss({ mitVerwalten: true, buttonText: 'Übernehmen', aktiv: false, onUebernehmen: () => {} }));
}

/**
 * Popover "Vorlagen einsetzen" für eine Woche (an der KW-Spalte).
 * @param {object} optionen
 * @param {HTMLElement} optionen.anker der KW-Button
 * @param {dayjs} optionen.montag Montag der Woche
 * @param {number} optionen.bestehend Anzahl vorhandener Bedarfszeilen in der Woche
 * @param {function} optionen.onFertig nach dem Einsetzen (Ansicht neu laden)
 */
async function oeffneWochenVorlagen({ anker, montag, bestehend, onFertig }) {
	const pop = oeffnePopover(anker, 'seite', 540);
	if (!pop) return;
	const vorlagen = await tagesvorlageService.holeTagesvorlagen();
	if (offen?.pop !== pop) return; // inzwischen geschlossen
	const kw = montag.add(14, 'hours').isoWeek();
	const sonntag = montag.add(6, 'day');
	const zeitraum = `${montag.format('D.')} – ${sonntag.format('D. MMMM')}`;
	// Vorausgewählt: alle Vorlagen mit mindestens einem Wochentag
	const auswahl = new Set(vorlagen.filter((v) => v.Wochentage.length > 0).map((v) => v._id));

	const rendere = () => {
		pop.innerHTML = '';
		pop.appendChild(baueKopf('bi-layers', 'Vorlagen einsetzen', `KW ${kw} · ${zeitraum} · jede Vorlage gilt für ihre Wochentage`));
		const hinweis = baueBestehendHinweis(bestehend, `KW ${kw}`);
		if (hinweis) pop.appendChild(hinweis);
		if (vorlagen.length === 0) {
			baueLeerHinweis(pop);
			return;
		}
		const liste = el('div', 'wt-list');
		vorlagen.forEach((vorlage) => {
			const k = kennzahlen(vorlage.Schichten);
			liste.appendChild(baueOption(vorlage, auswahl.has(vorlage._id), `${schichtenText(k.schichten)} · ${formatStunden(k.minuten)} h pro Tag`, true, () => {
				if (auswahl.has(vorlage._id)) auswahl.delete(vorlage._id); else auswahl.add(vorlage._id);
				rendere();
			}));
		});
		pop.appendChild(liste);

		// Vorschau: welche Vorlagen fallen auf welchen Tag
		pop.appendChild(el('div', 'wt-sec', `Ergebnis für KW ${kw}`));
		const tage = Array.from({ length: 7 }, (_, i) => ({
			tag: montag.add(i, 'day'),
			vorlagen: vorlagen.filter((v) => auswahl.has(v._id) && v.Wochentage.includes(i + 1)),
		}));
		const prev = el('div', 'wt-prev');
		tage.forEach(({ tag, vorlagen: vt }) => {
			const kachel = el('div', `wt-pd${vt.length === 0 ? ' off' : ''}${vt.length > 1 ? ' sum' : ''}`);
			kachel.appendChild(el('span', 'd', `${WOCHENTAGE[tag.isoWeekday() - 1]} ${tag.format('D.')}`));
			if (vt.length === 0) {
				kachel.appendChild(el('span', 'nm', 'Kein Bedarf'));
			} else {
				const seg = el('span', 'seg1');
				const anteil = 100 / vt.length;
				seg.style.background = vt.length === 1
					? farbe(vt[0])
					: `linear-gradient(90deg, ${vt.map((v, i) => `${farbe(v)} ${i * anteil}% ${(i + 1) * anteil}%`).join(', ')})`;
				kachel.append(seg, el('span', 'nm', vt[0].Name));
				vt.slice(1).forEach((v) => kachel.appendChild(el('span', 'plus', `+ ${v.Name}`)));
				kachel.appendChild(el('span', 'h', stundenKurz(minutenVon(vt))));
			}
			prev.appendChild(kachel);
		});
		pop.appendChild(prev);

		const mehrfach = tage.filter((t) => t.vorlagen.length > 1);
		if (mehrfach.length > 0) {
			const note = el('div', 'wt-note');
			note.innerHTML = '<i class="bi bi-plus-circle"></i>';
			note.appendChild(el('b', '', `${mehrfach.map((t) => WOCHENTAGE[t.tag.isoWeekday() - 1]).join(', ')}:`));
			note.appendChild(document.createTextNode(' mehrere Vorlagen — Schichten werden addiert.'));
			pop.appendChild(note);
		}

		const summe = tage.reduce((s, t) => s + minutenVon(t.vorlagen), 0);
		const einsaetze = tage.filter((t) => t.vorlagen.length > 0)
			.map((t) => ({ Datum: t.tag.format('YYYY-MM-DD'), TagesvorlageIDs: t.vorlagen.map((v) => v._id) }));
		pop.appendChild(baueFuss({
			mitVerwalten: true,
			buttonText: `Übernehmen · ${stundenKurz(summe)}`,
			aktiv: einsaetze.length > 0,
			onUebernehmen: () => setzeEin(einsaetze, onFertig),
		}));
		positioniere(pop, anker, 'seite');
	};
	rendere();
	positioniere(pop, anker, 'seite');
}

/**
 * Popover "Tagesvorlage einsetzen" für einen einzelnen Tag (z.B. Feiertag mit Wochenend-Bedarf).
 * @param {object} optionen
 * @param {HTMLElement} optionen.anker der Button in der Tageszelle
 * @param {dayjs} optionen.tag
 * @param {number} optionen.bestehend Anzahl vorhandener Bedarfszeilen am Tag
 * @param {function} optionen.onFertig nach dem Einsetzen (Ansicht neu laden)
 */
async function oeffneTagVorlagen({ anker, tag, bestehend, onFertig }) {
	const pop = oeffnePopover(anker, 'unten', 340);
	if (!pop) return;
	const vorlagen = await tagesvorlageService.holeTagesvorlagen();
	if (offen?.pop !== pop) return;
	const wochentag = tag.isoWeekday();
	const auswahl = new Set();

	const rendere = () => {
		pop.innerHTML = '';
		pop.appendChild(baueKopf('bi-layers', `${WOCHENTAGE[wochentag - 1]}, ${tag.format('D. MMMM')}`, 'Jede Vorlage wählbar — auch außerhalb ihrer Tage'));
		const hinweis = baueBestehendHinweis(bestehend, 'Der Tag');
		if (hinweis) pop.appendChild(hinweis);
		if (vorlagen.length === 0) {
			baueLeerHinweis(pop);
			return;
		}
		const liste = el('div', 'wt-list');
		vorlagen.forEach((vorlage) => {
			const k = kennzahlen(vorlage.Schichten);
			let tageText = 'ohne Wochentage';
			if (vorlage.Wochentage.includes(wochentag)) tageText = `Standard für ${WOCHENTAGE[wochentag - 1]}`;
			else if (vorlage.Wochentage.length > 0) tageText = `sonst ${wochentageText(vorlage.Wochentage)}`;
			liste.appendChild(baueOption(vorlage, auswahl.has(vorlage._id), `${tageText} · ${formatStunden(k.minuten)} h`, false, () => {
				if (auswahl.has(vorlage._id)) auswahl.delete(vorlage._id); else auswahl.add(vorlage._id);
				rendere();
			}));
		});
		pop.appendChild(liste);
		const gewaehlt = vorlagen.filter((v) => auswahl.has(v._id));
		pop.appendChild(baueFuss({
			mitVerwalten: false,
			buttonText: `Einsetzen · ${stundenKurz(minutenVon(gewaehlt))}`,
			aktiv: gewaehlt.length > 0,
			onUebernehmen: () => setzeEin([{ Datum: tag.format('YYYY-MM-DD'), TagesvorlageIDs: gewaehlt.map((v) => v._id) }], onFertig),
		}));
		positioniere(pop, anker, 'unten');
	};
	rendere();
	positioniere(pop, anker, 'unten');
}

/**
 * Popover "Vorwoche übernehmen" (an der KW-Spalte): zeigt je Tag, welcher Bedarf aus der Vorwoche kommt, und
 * übernimmt ihn erst per Button — immer ergänzend, ohne Event-Bedarf und ohne Zuordnungen.
 * @param {object} optionen
 * @param {HTMLElement} optionen.anker der KW-Button
 * @param {dayjs} optionen.montag Montag der Zielwoche
 * @param {number} optionen.bestehend Anzahl vorhandener Bedarfszeilen in der Zielwoche
 * @param {function} optionen.onFertig nach dem Übernehmen (Ansicht aktualisieren)
 */
async function oeffneVorwocheUebernehmen({ anker, montag, bestehend, onFertig }) {
	const pop = oeffnePopover(anker, 'seite', 540);
	if (!pop) return;
	const vorMontag = montag.subtract(7, 'day');
	const quelle = (await bedarfService.holeBedarfZeitraum(vorMontag, vorMontag.add(6, 'day'))).filter((z) => !z.EventID);
	if (offen?.pop !== pop) return; // inzwischen geschlossen
	const kw = montag.add(14, 'hours').isoWeek();
	const vorKw = vorMontag.add(14, 'hours').isoWeek();
	const bedarfeText = (n) => `${n} ${n === 1 ? 'Bedarf' : 'Bedarfe'}`;

	pop.appendChild(baueKopf('bi-copy', 'Vorwoche übernehmen', `KW ${vorKw} → KW ${kw} · ${montag.format('D.')} – ${montag.add(6, 'day').format('D. MMMM')} · ohne Events und Zuordnungen`));
	const hinweis = baueBestehendHinweis(bestehend, `KW ${kw}`);
	if (hinweis) pop.appendChild(hinweis);

	pop.appendChild(el('div', 'wt-sec', `Bedarf aus KW ${vorKw}`));
	const prev = el('div', 'wt-prev');
	for (let i = 0; i < 7; i += 1) {
		const quellTag = vorMontag.add(i, 'day').format('YYYY-MM-DD');
		const zeilen = quelle.filter((z) => dayjs(z.Datum).format('YYYY-MM-DD') === quellTag);
		const zielTag = montag.add(i, 'day');
		const kachel = el('div', `wt-pd${zeilen.length === 0 ? ' off' : ''}`);
		kachel.appendChild(el('span', 'd', `${WOCHENTAGE[i]} ${zielTag.format('D.')}`));
		if (zeilen.length === 0) {
			kachel.appendChild(el('span', 'nm', 'Kein Bedarf'));
		} else {
			const personen = zeilen.reduce((summe, z) => summe + z.BedarfAnzahl, 0);
			const minuten = zeilen.reduce((summe, z) => summe + dayjs(z.SchichtBis).diff(dayjs(z.SchichtVon), 'minute') * z.BedarfAnzahl, 0);
			kachel.append(el('span', 'nm', bedarfeText(zeilen.length)), el('span', 'pers', `${personen} Pers.`), el('span', 'h', stundenKurz(minuten)));
		}
		prev.appendChild(kachel);
	}
	pop.appendChild(prev);
	if (quelle.length === 0) {
		const leer = el('div', 'wt-note info');
		leer.innerHTML = '<i class="bi bi-info-circle"></i>';
		leer.appendChild(document.createTextNode(`KW ${vorKw} hat keinen Bedarf (ohne Events) — nichts zu übernehmen.`));
		leer.style.marginTop = '10px';
		pop.appendChild(leer);
	}

	pop.appendChild(baueFuss({
		mitVerwalten: false,
		buttonText: `Übernehmen · ${bedarfeText(quelle.length)}`,
		aktiv: quelle.length > 0,
		onUebernehmen: async () => {
			const ergebnis = await bedarfService.uebernehmeVorwoche(montag);
			if (!ergebnis) return;
			zeigeKleineNachricht(`${bedarfeText(ergebnis.angelegt)} aus KW ${vorKw} übernommen`, 1);
			schliesse();
			await onFertig();
		},
	}));
	positioniere(pop, anker, 'seite');
}

export { oeffneWochenVorlagen, oeffneTagVorlagen, oeffneVorwocheUebernehmen };
